import comp from '../components/barrel';
import advisorContactReportForm from '../js/advisorContactReportForm';
import listReturnState from '../js/listReturnState';
import svg from '../js/svg';

const EP = 'advisors/get-advisor-group-details';
const USERS_EP = 'advisors/get-advisor-group-users';
const ALLOC_EP = 'advisors/alloc-advisor-group-access';
const TOGGLE_MEMBER_EP = 'advisors/toggl-group-member';
const UPD_GROUP_EP = 'advisors/upd-advisor-group';
const PAGE_SIZE = 50;
const MEMBER_COL_COUNT = 13;

function cellText(val) {
  return val != null && String(val).trim() !== '' ? String(val) : '';
}

function ynText(val) {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return '—';
}

function isYnTrue(val) {
  return val === true || val === 'true' || val === 1 || val === '1';
}

function createdByName(row) {
  return `${cellText(row.f_name)} ${cellText(row.s_name)}`.trim() || '—';
}

function groupUserLabel(row) {
  const name = `${cellText(row.f_name)} ${cellText(row.s_name)}`.trim() || '—';
  const role = cellText(row.role);
  return role ? `${name} (${role})` : name;
}

async function dateTimeText(val) {
  if (val == null || String(val).trim() === '') return '—';
  return await U.formatDateTime(val);
}

async function dateText(val) {
  if (val == null || String(val).trim() === '') return '—';
  return await U.formatDate(val);
}

function parseDateOnly(val) {
  if (val == null || String(val).trim() === '') return null;
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function styleOverdueCrmFollowUp(cell, crmFollowUp, lastContactReport) {
  const crm = parseDateOnly(crmFollowUp);
  const lastContact = parseDateOnly(lastContactReport);
  if (!crm || !lastContact) return;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (crm < today && lastContact < crm) {
    cell.classList.add('font-bold');
    cell.style.color = '#dc2626';
  }
}

function setFieldFeedback(el, message) {
  if (!el) return;
  const fb = document.querySelector(`[fb-for="${el.id}"]`);
  if (fb) fb.textContent = message || '';
  if (message) el.classList.add('input-error');
  else el.classList.remove('input-error');
}

function addDetailField(parent, label, value) {
  const wrap = document.createElement('div');
  const heading = document.createElement('h6');
  heading.className = 'text-primary';
  heading.textContent = label;
  const text = document.createElement('p');
  text.textContent = value;
  wrap.appendChild(heading);
  wrap.appendChild(text);
  parent.appendChild(wrap);
}

function appendAccordionHeadingField(parent, label, value, valueId) {
  const row = U.divAtt('div');
  const lbl = U.divAtt('strong');
  lbl.textContent = `${label}: `;
  row.appendChild(lbl);
  const val = document.createElement('span');
  if (valueId) val.id = valueId;
  val.textContent = value != null && value !== '' ? String(value) : '—';
  row.appendChild(val);
  parent.appendChild(row);
}

function showDetailMessage(detail, message) {
  if (!detail) return;
  const p = U.divAtt('p', 'class', 'text-gray-600');
  p.textContent = message;
  detail.appendChild(p);
}

function emptyPagination() {
  return {total_count: 0, page_size: PAGE_SIZE, current_page: 1, total_pages: 0};
}

export default {
  groupUsers: [],

  async prepare() {
    const listRestore = this.vars && this.vars.listRestore;
    if (listRestore && listRestore.h_advisor_group_id) {
      this.vars.h_advisor_group_id = listRestore.h_advisor_group_id;
      this.vars.selected_page = parseInt(listRestore.selected_page, 10) || 1;
    }
    const groupId = this.vars && this.vars.h_advisor_group_id;
    this.vars.selected_page = parseInt(this.vars.selected_page, 10) || 1;
    if (groupId) {
      this.recipe.methods.push(
        await O.mPOST(EP, {
          h_advisor_group_id: groupId,
          page: this.vars.selected_page,
          limit: PAGE_SIZE,
        })
      );
    }
  },

  async render() {
    const usersRes = await O.PREP([await O.mPOST(USERS_EP, {})]);
    const usersPayload = (usersRes && usersRes[USERS_EP]) || {};
    this.groupUsers = Array.isArray(usersPayload.data) ? usersPayload.data : [];

    const rec = this.recipe.methods;
    let data = {};
    if (rec.length) {
      data = await O.PREP(rec);
    }
    await this.loadPage(data);
  },

  async loadPage(data) {
    const back = listReturnState.take('ViewAdvisorGroup') || this.listReturnBack || {
      listPage: 'AdvisorGroups',
      state: {},
    };
    this.listReturnBack = back;
    listReturnState.mountBackToListBar('Back to advisor groups', back);

    const detail = U.id('advisorGroupDetail');
    if (detail) detail.innerHTML = '';

    const groupId = this.vars && this.vars.h_advisor_group_id;
    if (!groupId) {
      showDetailMessage(detail, 'No advisor group selected. Open a group from the list.');
      await this.buildMembersTable([]);
      await this.buildPaginationButtons(emptyPagination());
      return;
    }

    const res = data[EP] || {};
    const payload = res.data || {};
    const group = payload.group || null;
    const members = Array.isArray(payload.members) ? payload.members : [];
    const allocatedUsers = Array.isArray(payload.allocated_users) ? payload.allocated_users : [];
    const editable = isYnTrue(payload.editable_yn);
    this.canEditAdvisorGroup = editable;
    const paginationState = res.pagination || {
      total_count: 0,
      page_size: PAGE_SIZE,
      current_page: this.vars.selected_page || 1,
      total_pages: 0,
    };

    if (!group) {
      showDetailMessage(detail, 'Could not load this advisor group. Try again from the list.');
      await this.buildMembersTable([]);
      await this.buildPaginationButtons(emptyPagination());
      return;
    }

    if (detail) {
      await this.buildGroupDetailAccordion(detail, group, this.groupUsers, allocatedUsers, editable);
    }

    await this.buildMembersTable(members);
    this.vars.selected_page = paginationState.current_page || this.vars.selected_page || 1;
    await this.buildPaginationButtons(paginationState);
  },

  async buildGroupDetailAccordion(container, group, users, allocatedUsers, editable) {
    const elID = 'advisorGroupDetailAccordion';

    const header = U.divAtt('div', 'class', 'flex justify-between items-start gap-4 cursor-pointer');
    header.setAttribute('onclick', `thsPage.toggleAdvisorGroupDetailAccordion('${elID}')`);

    const heading = U.divAtt('div', 'class', 'flex-1 grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4');
    appendAccordionHeadingField(heading, 'Group name', cellText(group.group_name) || '—', 'advisorGroupHeadingName');
    appendAccordionHeadingField(heading, 'Members count', group.user_count != null ? String(group.user_count) : '—');
    appendAccordionHeadingField(heading, 'Type', cellText(group.group_type) || '—');
    appendAccordionHeadingField(heading, 'Private', ynText(group.private_yn));

    const accorArrow = await svg.chevronUp(`${elID}_arrow`);
    accorArrow.classList.add('arrowDown', 'shrink-0');

    header.appendChild(heading);
    header.appendChild(accorArrow);
    container.appendChild(header);

    const contentDiv = U.divAtt('div', 'class', 'hide mt-4');
    contentDiv.id = `${elID}_content`;

    const body = U.divAtt('div', 'class', 'advisor-group-detail-body pt-4');
    const left = U.divAtt('div', 'class', 'advisor-group-detail-meta');
    addDetailField(left, 'Created by', createdByName(group));
    addDetailField(left, 'Created', await dateTimeText(group.created_date));
    addDetailField(left, 'Updated', await dateTimeText(group.updated_date));

    const groupUsers = Array.isArray(users) ? users : [];
    const allocated = Array.isArray(allocatedUsers) ? allocatedUsers : [];
    const usersCol = U.divAtt('div', 'class', 'advisor-group-detail-users');
    if (editable) {
      usersCol.appendChild(await this.buildUsersMultiSelect(groupUsers, allocated));

      const updateBtn = document.createElement('button');
      updateBtn.type = 'button';
      updateBtn.id = 'advisorGroupUsersUpdateBtn';
      updateBtn.className = 'btn advisor-group-users-update-btn';
      updateBtn.textContent = 'Update';
      updateBtn.disabled = true;
      updateBtn.setAttribute('onclick', 'thsPage.updateAdvisorGroupAllocatedUsers()');
      usersCol.appendChild(updateBtn);
    } else {
      usersCol.appendChild(this.buildAllocatedUsersReadOnly(allocated));
    }

    const descCol = U.divAtt('div', 'class', 'advisor-group-detail-description');
    const groupName = cellText(group.group_name);
    const description = cellText(group.group_description);
    this.groupNameBaseline = groupName;
    this.groupDescriptionBaseline = description;
    this.canEditGroupDescription = !!editable;

    if (editable) {
      const nameField = await comp.inputText('advisorGroupName', '', 'Group name', 1, 'syncAdvisorGroupDescriptionUpdateBtn', '');
      nameField.classList.remove('col-span-full');
      const nameInput = nameField.querySelector('#advisorGroupName');
      if (nameInput) nameInput.value = groupName;
      descCol.appendChild(nameField);

      const descField = await comp.textarea('advisorGroupDescription', description, 'Description', 0);
      descField.classList.remove('col-span-full');
      const descInput = descField.querySelector('#advisorGroupDescription');
      if (descInput) {
        descInput.setAttribute('oninput', 'thsPage.syncAdvisorGroupDescriptionUpdateBtn()');
      }
      descCol.appendChild(descField);

      const descUpdateBtn = document.createElement('button');
      descUpdateBtn.type = 'button';
      descUpdateBtn.id = 'advisorGroupDescriptionUpdateBtn';
      descUpdateBtn.className = 'btn advisor-group-users-update-btn';
      descUpdateBtn.textContent = 'Update';
      descUpdateBtn.disabled = true;
      descUpdateBtn.setAttribute('onclick', 'thsPage.updateAdvisorGroupDescription()');
      descCol.appendChild(descUpdateBtn);
    } else {
      addDetailField(descCol, 'Group name', groupName || '—');
      addDetailField(descCol, 'Description', description || '—');
    }

    body.appendChild(left);
    body.appendChild(usersCol);
    body.appendChild(descCol);
    contentDiv.appendChild(body);
    container.appendChild(contentDiv);
  },

  buildAllocatedUsersReadOnly(users) {
    const wrap = U.divAtt('div', 'class', 'advisor-group-allocated-readonly');
    const label = document.createElement('h6');
    label.className = 'text-primary';
    label.textContent = 'Allocated to users';
    wrap.appendChild(label);

    const rows = Array.isArray(users) ? users : [];
    if (!rows.length) {
      const empty = U.divAtt('p', 'class', 'text-gray-600');
      empty.textContent = 'No allocated users.';
      wrap.appendChild(empty);
      return wrap;
    }

    const list = document.createElement('ul');
    list.className = 'advisor-group-allocated-list';
    rows.forEach((row) => {
      const item = document.createElement('li');
      item.textContent = groupUserLabel(row);
      list.appendChild(item);
    });
    wrap.appendChild(list);
    return wrap;
  },

  async buildUsersMultiSelect(users, allocatedUsers) {
    const selected = new Set(
      (Array.isArray(allocatedUsers) ? allocatedUsers : [])
        .map((row) => (row && row.h_user_id != null ? String(row.h_user_id) : ''))
        .filter(Boolean)
    );
    const wrap = U.divAtt('div', 'class', 'input-group advisor-group-user-multiselect');
    wrap.id = 'advisorGroupUsersSelect';

    const label = document.createElement('label');
    label.setAttribute('for', 'advisorGroupUsersToggle');
    label.textContent = 'Allocated to users';
    wrap.appendChild(label);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.id = 'advisorGroupUsersToggle';
    toggle.className = 'advisor-group-user-multiselect__toggle';
    toggle.setAttribute('aria-haspopup', 'listbox');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('onclick', 'thsPage.toggleAdvisorGroupUsersDropdown(event)');

    const toggleText = U.divAtt('span', 'id', 'advisorGroupUsersToggleText');
    toggleText.textContent = users.length ? 'Select users' : 'No users found';
    const toggleArrow = await svg.chevronUp('advisorGroupUsersArrow');
    toggleArrow.classList.add('arrowDown', 'shrink-0');
    toggle.appendChild(toggleText);
    toggle.appendChild(toggleArrow);
    wrap.appendChild(toggle);

    const panel = U.divAtt('div', 'class', 'advisor-group-user-multiselect__panel hide');
    panel.id = 'advisorGroupUsersPanel';
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-multiselectable', 'true');
    panel.setAttribute('onclick', 'event.stopPropagation()');

    if (!users.length) {
      const empty = U.divAtt('p', 'class', 'advisor-group-user-multiselect__empty');
      empty.textContent = 'No users found.';
      panel.appendChild(empty);
    } else {
      users.forEach((row, idx) => {
        if (!row || row.h_user_id == null) return;
        const id = String(row.h_user_id);
        const optionLabel = groupUserLabel(row);

        const option = U.divAtt('label', 'class', 'advisor-group-user-multiselect__option');
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = `advisorGroupUser_${idx}`;
        checkbox.name = 'advisorGroupUsers';
        checkbox.value = id;
        checkbox.dataset.userId = id;
        checkbox.dataset.label = optionLabel;
        checkbox.checked = selected.has(id);
        checkbox.setAttribute('onchange', 'thsPage.onAdvisorGroupUsersChange()');

        const text = document.createElement('span');
        text.textContent = optionLabel;

        option.appendChild(checkbox);
        option.appendChild(text);
        panel.appendChild(option);
      });
    }

    wrap.appendChild(panel);
    this.allocatedUsersBaseline = this.selectedAdvisorGroupUserIds(wrap).join(',');
    this.syncAdvisorGroupUsersToggleText();
    this.bindAdvisorGroupUsersOutsideClick();
    return wrap;
  },

  bindAdvisorGroupUsersOutsideClick() {
    if (this._advisorGroupUsersOutsideBound) return;
    this._advisorGroupUsersOutsideBound = true;
    document.addEventListener('click', (e) => {
      const wrap = U.id('advisorGroupUsersSelect');
      if (!wrap || wrap.contains(e.target)) return;
      this.closeAdvisorGroupUsersDropdown();
    });
  },

  toggleAdvisorGroupUsersDropdown(e) {
    if (e) e.stopPropagation();
    const panel = U.id('advisorGroupUsersPanel');
    const toggle = U.id('advisorGroupUsersToggle');
    const arrow = U.id('advisorGroupUsersArrow');
    if (!panel || !toggle) return;

    if (panel.classList.contains('hide')) {
      U.unhide('advisorGroupUsersPanel');
      toggle.setAttribute('aria-expanded', 'true');
      if (arrow) arrow.classList.remove('arrowDown');
    } else {
      this.closeAdvisorGroupUsersDropdown();
    }
  },

  closeAdvisorGroupUsersDropdown() {
    const panel = U.id('advisorGroupUsersPanel');
    const toggle = U.id('advisorGroupUsersToggle');
    const arrow = U.id('advisorGroupUsersArrow');
    if (panel) U.hide('advisorGroupUsersPanel');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (arrow) arrow.classList.add('arrowDown');
  },

  selectedAdvisorGroupUserIds(root) {
    const wrap = root || U.id('advisorGroupUsersSelect');
    if (!wrap) return [];
    return Array.from(wrap.querySelectorAll('input[name="advisorGroupUsers"]:checked'))
      .map((cb) => String(cb.dataset.userId || cb.value))
      .filter(Boolean)
      .sort();
  },

  onAdvisorGroupUsersChange() {
    this.syncAdvisorGroupUsersToggleText();
    this.syncAdvisorGroupUsersUpdateBtn();
  },

  syncAdvisorGroupUsersToggleText() {
    const textEl = U.id('advisorGroupUsersToggleText');
    const wrap = U.id('advisorGroupUsersSelect');
    if (!textEl || !wrap) return;
    const checked = wrap.querySelectorAll('input[name="advisorGroupUsers"]:checked');
    if (!checked.length) {
      textEl.textContent = 'Select users';
      return;
    }
    if (checked.length === 1) {
      textEl.textContent = checked[0].dataset.label || '1 user selected';
      return;
    }
    textEl.textContent = `${checked.length} users selected`;
  },

  syncAdvisorGroupUsersUpdateBtn() {
    const btn = U.id('advisorGroupUsersUpdateBtn');
    if (!btn) return;
    const usersDirty = this.selectedAdvisorGroupUserIds().join(',') !== (this.allocatedUsersBaseline || '');
    btn.disabled = !usersDirty;
  },

  async updateAdvisorGroupAllocatedUsers() {
    const btn = U.id('advisorGroupUsersUpdateBtn');
    const groupId = parseInt(this.vars && this.vars.h_advisor_group_id, 10);
    if (!Number.isInteger(groupId) || groupId <= 0) return;

    const currentKey = this.selectedAdvisorGroupUserIds().join(',');
    if (currentKey === (this.allocatedUsersBaseline || '')) {
      if (btn) btn.disabled = true;
      return;
    }

    const allocated_users = this.selectedAdvisorGroupUserIds().map((id) => parseInt(id, 10)).filter((id) => Number.isInteger(id) && id > 0);
    if (btn) btn.disabled = true;

    try {
      const res = await O.PREP([
        await O.mPOST(ALLOC_EP, {
          h_advisor_group_id: groupId,
          allocated_users,
        }),
      ]);
      const allocRes = res[ALLOC_EP] || {};
      if (allocRes.status === 'OK') {
        this.allocatedUsersBaseline = this.selectedAdvisorGroupUserIds().join(',');
        this.syncAdvisorGroupUsersUpdateBtn();
        this.closeAdvisorGroupUsersDropdown();
        comp.toast('Allocated users updated');
        return;
      }
      this.syncAdvisorGroupUsersUpdateBtn();
      const err = allocRes.data && allocRes.data.error;
      comp.toast(err || 'Unable to update allocated users');
    } catch (e) {
      this.syncAdvisorGroupUsersUpdateBtn();
      comp.toast('Unable to update allocated users');
    }
  },

  syncAdvisorGroupDescriptionUpdateBtn() {
    const btn = U.id('advisorGroupDescriptionUpdateBtn');
    if (!btn) return;
    const nameEl = U.id('advisorGroupName');
    const descEl = U.id('advisorGroupDescription');
    const nameDirty = !!(nameEl && nameEl.value !== (this.groupNameBaseline || ''));
    const descDirty = !!(descEl && descEl.value !== (this.groupDescriptionBaseline || ''));
    btn.disabled = !(this.canEditGroupDescription && (nameDirty || descDirty));
  },

  async updateAdvisorGroupDescription() {
    const btn = U.id('advisorGroupDescriptionUpdateBtn');
    const nameEl = U.id('advisorGroupName');
    const descEl = U.id('advisorGroupDescription');
    const groupId = parseInt(this.vars && this.vars.h_advisor_group_id, 10);
    if (!Number.isInteger(groupId) || groupId <= 0 || !nameEl || !descEl) return;

    const group_name = nameEl.value;
    const description = descEl.value;
    const nameDirty = group_name !== (this.groupNameBaseline || '');
    const descDirty = description !== (this.groupDescriptionBaseline || '');
    if (!nameDirty && !descDirty) {
      if (btn) btn.disabled = true;
      return;
    }

    if (!group_name.trim()) {
      setFieldFeedback(nameEl, 'Group name is required');
      this.syncAdvisorGroupDescriptionUpdateBtn();
      return;
    }
    setFieldFeedback(nameEl, '');

    if (btn) btn.disabled = true;

    try {
      const res = await O.PREP([
        await O.mPOST(UPD_GROUP_EP, {
          h_advisor_group_id: groupId,
          group_name,
          description,
        }),
      ]);
      const updRes = res[UPD_GROUP_EP] || {};
      if (updRes.status === 'OK') {
        const savedName = updRes.data && updRes.data.group_name;
        const savedDesc = updRes.data && updRes.data.group_description;
        this.groupNameBaseline = savedName != null ? String(savedName) : group_name;
        this.groupDescriptionBaseline = savedDesc != null ? String(savedDesc) : description;
        if (nameEl.value !== this.groupNameBaseline) nameEl.value = this.groupNameBaseline;
        if (descEl.value !== this.groupDescriptionBaseline) descEl.value = this.groupDescriptionBaseline;
        const headingName = U.id('advisorGroupHeadingName');
        if (headingName) headingName.textContent = this.groupNameBaseline || '—';
        this.syncAdvisorGroupDescriptionUpdateBtn();
        comp.toast('Group updated');
        return;
      }
      this.syncAdvisorGroupDescriptionUpdateBtn();
      const err = updRes.data && updRes.data.error;
      comp.toast(err || 'Unable to update group');
    } catch (e) {
      this.syncAdvisorGroupDescriptionUpdateBtn();
      comp.toast('Unable to update group');
    }
  },

  toggleAdvisorGroupDetailAccordion(elID) {
    const contentDiv = U.id(`${elID}_content`);
    const arrow = U.id(`${elID}_arrow`);
    if (!contentDiv || !arrow) return;

    if (contentDiv.classList.contains('hide')) {
      U.unhide(`${elID}_content`);
      arrow.classList.remove('arrowDown');
    } else {
      U.hide(`${elID}_content`);
      arrow.classList.add('arrowDown');
    }
  },

  async getMemberList(fromPagination) {
    const groupId = this.vars && this.vars.h_advisor_group_id;
    if (!groupId) return;
    if (!fromPagination) {
      this.vars.selected_page = 1;
    }
    const page = parseInt(this.vars.selected_page, 10) || 1;
    const res = await O.PREP([
      await O.mPOST(EP, {
        h_advisor_group_id: groupId,
        page,
        limit: PAGE_SIZE,
      }),
    ]);
    const listData = res[EP] || {};
    const payload = listData.data || {};
    const members = Array.isArray(payload.members) ? payload.members : [];
    const paginationState = listData.pagination || {
      total_count: 0,
      page_size: PAGE_SIZE,
      current_page: page,
      total_pages: 0,
    };
    this.canEditAdvisorGroup = isYnTrue(payload.editable_yn);
    await this.buildMembersTable(members);
    this.vars.selected_page = paginationState.current_page || page;
    await this.buildPaginationButtons(paginationState);
  },

  async buildPaginationButtons(paginationState) {
    const totalPages = paginationState.total_pages || 0;
    const wrapper = U.id('advisorGroupMembersPaginationWrapper');
    if (totalPages > 0) {
      await comp.paginationButtons({
        totalPages,
        wrapperID: 'advisorGroupMembersPaginationWrapper',
      });
      const pageButtons = document.querySelectorAll('#advisorGroupMembersPaginationWrapper .pageButton');
      pageButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          thsPage.vars.selected_page = Number(e.target.getAttribute('value'));
          this.getMemberList(true);
        });
      });
    } else if (wrapper) {
      wrapper.innerHTML = '';
    }

    const totalRecords = paginationState.total_count != null ? paginationState.total_count : '-';
    const pages = paginationState.total_pages != null ? paginationState.total_pages : '-';
    const totalRecordsEl = U.id('advisorGroupMembersTotalRecordsDisplay');
    const totalPagesEl = U.id('advisorGroupMembersTotalPagesDisplay');
    if (totalRecordsEl) totalRecordsEl.textContent = totalRecords;
    if (totalPagesEl) totalPagesEl.textContent = pages;
  },

  buildMembersHead() {
    const tableHead = U.id('advisorGroupMembersTableHead');
    if (!tableHead) return;
    tableHead.innerHTML = '';
    const headRow = tableHead.insertRow();
    ['', 'First name', 'Last name', 'Email', 'Broker number', 'Active in group', 'User active', 'Retired', 'Last contact report', 'CRM follow up', 'Recent application', 'Will count', ''].forEach((label, i) => {
      const cell = headRow.insertCell(i);
      cell.innerHTML = label;
    });
  },

  async buildMembersTable(listData) {
    this.buildMembersHead();
    const tbody = U.id('advisorGroupMembersTableBody');
    if (!tbody) return;
    tbody.innerHTML = '';

    if (!listData.length) {
      const tr = tbody.insertRow();
      const cell = tr.insertCell(0);
      cell.colSpan = MEMBER_COL_COUNT;
      cell.innerHTML = 'No users in this group.';
      return;
    }

    for (const row of listData) {
      const tr = tbody.insertRow();

      const viewAdvisor = tr.insertCell(0);
      viewAdvisor.className = 'advisor-icon-cell';
      if (row.advisor_uuid) {
        const linkSVG = svg.linkNewPage();
        linkSVG.style.cursor = 'pointer';
        linkSVG.setAttribute('role', 'button');
        linkSVG.setAttribute('title', 'View advisor');
        linkSVG.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.listReturnBack) {
            listReturnState.save('ViewAdvisorGroup', this.listReturnBack);
          }
          listReturnState.save('ViewAdvisor', {
            listPage: 'ViewAdvisorGroup',
            state: {
              h_advisor_group_id: this.vars && this.vars.h_advisor_group_id,
              selected_page: this.vars && this.vars.selected_page,
            },
          });
          O.PREP('ViewAdvisor', `advisor_uuid=${row.advisor_uuid}`);
        });
        viewAdvisor.appendChild(linkSVG);
      }

      tr.insertCell(1).textContent = cellText(row.advisor_f_name) || '—';
      tr.insertCell(2).textContent = cellText(row.advisor_s_name) || '—';
      tr.insertCell(3).textContent = cellText(row.email_address) || '—';
      tr.insertCell(4).textContent = cellText(row.broker_number) || '—';

      const memberActive = tr.insertCell(5);
      const memberActiveYn = isYnTrue(row.member_active_yn);
      if (this.canEditAdvisorGroup) {
        const memberId = row.d_advisor_group_id != null ? String(row.d_advisor_group_id) : '';
        const memberToggle = await comp.toggle_v2({
          elID: `memberActiveYn_${memberId || cellText(row.h_user_id)}`,
          isChecked: memberActiveYn,
          labelName: '',
        });
        const memberLabel = memberToggle.querySelector('.tgglLable');
        if (memberLabel) memberLabel.remove();
        const memberCheckbox = memberToggle.querySelector('input[type="checkbox"]');
        if (memberCheckbox && memberId) {
          memberCheckbox.dataset.dAdvisorGroupId = memberId;
          memberCheckbox.addEventListener('change', () => {
            this.toggleGroupMember(memberCheckbox);
          });
        }
        memberActive.appendChild(memberToggle);
      } else {
        memberActive.appendChild(await comp.statusTag(memberActiveYn ? 'Active' : 'Inactive'));
      }

      const userActive = tr.insertCell(6);
      userActive.appendChild(await comp.statusTag(isYnTrue(row.user_active_yn) ? 'Active' : 'Inactive'));

      tr.insertCell(7).textContent = ynText(row.retired_yn);
      tr.insertCell(8).textContent = await dateText(row.last_contact_report);
      const crmFollowUp = tr.insertCell(9);
      crmFollowUp.textContent = await dateText(row.crm_follow_up);
      styleOverdueCrmFollowUp(crmFollowUp, row.crm_follow_up, row.last_contact_report);

      tr.insertCell(10).textContent = await dateText(row.latest_application_created_date);

      const willCount = tr.insertCell(11);
      willCount.textContent =
        row.will_count != null && String(row.will_count).trim() !== '' ? String(row.will_count) : '—';

      const contactCell = tr.insertCell(12);
      contactCell.className = 'advisor-icon-cell';
      const contactIcon = svg.personSpeech();
      contactIcon.classList.add('cursor-pointer', 'text-primary');
      contactIcon.style.cursor = 'pointer';
      contactIcon.setAttribute('role', 'button');
      contactIcon.setAttribute('title', 'Contact report');
      contactIcon.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openContactReportForm(row);
      });
      contactCell.appendChild(contactIcon);
    }
  },

  async openContactReportForm(row) {
    await advisorContactReportForm.open({
      advisor_user_id: row && row.h_user_id,
      d_advisor_group_id: row && row.d_advisor_group_id,
      h_advisor_group_id: this.vars && this.vars.h_advisor_group_id,
      fromAdvisorGroup: true,
      onSuccess: () => this.getMemberList(true),
    });
  },

  async toggleGroupMember(checkbox) {
    if (!checkbox) return;
    const previous = !checkbox.checked;
    if (!this.canEditAdvisorGroup) {
      checkbox.checked = previous;
      return;
    }
    const d_advisor_group_id = parseInt(checkbox.dataset.dAdvisorGroupId, 10);
    const member_active_yn = !!checkbox.checked;

    if (!Number.isInteger(d_advisor_group_id) || d_advisor_group_id <= 0) {
      checkbox.checked = previous;
      return;
    }

    checkbox.disabled = true;
    try {
      const res = await O.PREP([
        await O.mPOST(TOGGLE_MEMBER_EP, {
          d_advisor_group_id,
          member_active_yn,
        }),
      ]);
      const result = res[TOGGLE_MEMBER_EP] || {};
      if (result.status === 'OK') {
        comp.toast('Member updated');
      } else {
        checkbox.checked = previous;
        const err = result.data && result.data.error;
        comp.toast(err || 'Unable to update member');
      }
    } catch (e) {
      checkbox.checked = previous;
      comp.toast('Unable to update member');
    } finally {
      checkbox.disabled = !this.canEditAdvisorGroup;
    }
  },
};
